import { useCallback, useEffect } from "react";
import { useRoomContext } from "@livekit/components-react";
import { DisconnectReason, RoomEvent } from "livekit-client";
import type { ProviderNotificationsListenerOptions } from "../types";
import { ACTION_TYPES } from "./helpers";
import { useLivekitCamera } from "./useLivekitCamera";
import { useLivekitLocalParticipant } from "./useLivekitLocalParticipant";
import { useLivekitMicrophone } from "./useLivekitMicrophone";

const decoder = new TextDecoder();

export const useLivekitNotificationsListener = ({
  onRoomUpdate,
  onEndStream,
}: ProviderNotificationsListenerOptions) => {
  const room = useRoomContext();
  const localParticipant = useLivekitLocalParticipant();
  const { toggleMicrophone, isMicrophoneEnabled } = useLivekitMicrophone();
  const { toggleCamera, isCameraEnabled } = useLivekitCamera();

  const handleDataReceived = useCallback(
    (data: Uint8Array) => {
      try {
        const strData = decoder.decode(data);
        const action = JSON.parse(strData);
        const { type } = action || {};

        switch (type) {
          case ACTION_TYPES.UPDATE_ROOM:
            onRoomUpdate && onRoomUpdate();
            break;
          case ACTION_TYPES.MUTE_MICROPHONE:
            if (isMicrophoneEnabled) {
              void toggleMicrophone();
            }
            break;
          case ACTION_TYPES.DISABLE_CAMERA:
            if (isCameraEnabled) {
              void toggleCamera();
            }
            break;
          case ACTION_TYPES.LOWER_HAND:
            void localParticipant.setAttributes({
              isHandRaised: "false",
            });
            break;
          case ACTION_TYPES.STOP_SCREENSHARE:
            void localParticipant.setScreenShareEnabled(false);
            break;
        }
      } catch (err: unknown) {
        if (err instanceof Error) {
          console.error("Could not decode received data - ", err.message);
        }
      }
    },
    [isMicrophoneEnabled, isCameraEnabled],
  );

  useEffect(() => {
    const onRoomDisconnected = (reason?: DisconnectReason) => {
      if (
        reason === DisconnectReason.ROOM_DELETED ||
        reason === DisconnectReason.PARTICIPANT_REMOVED
      ) {
        onEndStream && onEndStream();
      }
    };

    if (room) {
      room.on(RoomEvent.DataReceived, handleDataReceived);
      room.on(RoomEvent.Disconnected, onRoomDisconnected);
    }

    return () => {
      if (room) {
        room.off(RoomEvent.DataReceived, handleDataReceived);
        room.off(RoomEvent.Disconnected, onRoomDisconnected);
      }
    };
  }, [onEndStream, onRoomUpdate, handleDataReceived, room]);
};
